import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { remove } from 'lodash-es';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import * as CommunityAPI from '@/apis/community';
import { getHttpErrorMessage, getHttpErrorStatus } from '@/apis/http-errors';
import {
  COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP,
  undoAgentActionPopupTitle,
} from '@/app/community/constants';
import {
  AI_CATEGORY_FILTER_OPTION,
  COMMUNITY_INTERACTION_RULE_TOASTS,
  MESSAGE_RULE_MIXPANEL_CONSTANTS,
  RULE_ACTION_KEYS,
} from '@/app/community/constants/inboxAutomation';
import {
  createOrEditMessageRuleEvent,
  messageRuleInteractionsEvent,
  messageRulesSelectedEvent,
  undoMessageRuleEvent,
} from '@/app/community/utils/mixpanel-events';
import { isVisionAgentEnabled } from '@/app/community/utils/visionAgentFlag';
import { useCommunityMessageRuleBuilder } from '@/composables/community/useCommunityRuleBuilder';
import { useAuthStore } from '@/stores/auth';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useIdentityStore } from '@/stores/identity';
import { logger } from '@/utils/logger';

export const useCommunityInteractionRulesStore = defineStore('communityInteractionRules', () => {
  const identityStore = useIdentityStore();
  const { showConfirmationDialog: undoRuleActionDialog } = useConfirmationDialog({
    group: 'global',
    acceptSeverity: 'primary',
  });
  const { addToast } = useToast({ life: 3000 });
  const { launchDrawer } = useDrawer();

  const interactionRules = ref([]);
  const selectedBrandIdForInteractionRules = ref();

  const editedRuleId = ref();
  const pending = ref({
    interactionRules: true,
    interactionRule: false,
    aiCategories: true,
  });
  const switchedToNestedDrawer = ref(false);

  const aiCategories = ref([]);
  const pendingAiCategory = ref();

  const editedRule = computed(() => {
    if (editedRuleId.value) {
      return interactionRules.value.find((r) => r.id === editedRuleId.value);
    }
    return undefined;
  });

  const selectedBrandId = computed(
    () => selectedBrandIdForInteractionRules.value ?? identityStore.currentBrand?.id,
  );

  const selectedBrand = computed(
    () => identityStore.identityBrandsById[selectedBrandId.value] ?? identityStore.currentBrand,
  );

  const selectedBrandName = computed(
    () =>
      identityStore.identityBrandsById[selectedBrandId.value]?.name ??
      identityStore.currentBrand?.name,
  );

  const aiCategoriesByBrand = computed(() => groupBy(aiCategories.value, 'brandId'));

  function getBrandAiCategories(brandId) {
    return aiCategoriesByBrand.value[brandId] ?? [];
  }

  const selectedBrandAiCategories = computed(() => getBrandAiCategories(selectedBrandId.value));

  const interactionRulesByBrand = computed(() => {
    if (!interactionRules.value?.length) {
      return {};
    }
    return groupBy(interactionRules.value, 'brandId');
  });

  function getSelectedBrandInteractionRules(brandId = selectedBrandId.value) {
    const brandInteractionRules = interactionRulesByBrand.value?.[brandId] ?? [];
    return orderBy(brandInteractionRules, [(r) => r.lastModifiedAt ?? r.createdAt], ['desc']);
  }

  const {
    actions,
    conditionGroups,
    isFiltersComplete,
    isActionsComplete,
    hasRepliedFilter,
    filterMatchedMessagesCount,
    ruleDetail,
    ruleChannelSet,
    isSingleChannelRule,
    hasMultiChannelSelection,
    isChannelFilterOrCondition,
    sendDmFromCommentValidation,
    likeCommentsValidation,
    buildJSONRule,
    updateGroupOperator,
    addGroupAfter,
    updateGroup,
    removeFilterGroup,
    buildJSONRuleFilters,
    buildJSONRuleActions,
    checkGroupHasCompleteItem,
    clearRuleBuilder,
    updateFilterMatchCount,
    setRuleActionsFromJSON,
    setRuleFiltersFromJSON,
    isRuleDetailChanged,
    getFilterUsed,
    getFilterGroups,
    clearFilterMessageCountAndError,
  } = useCommunityMessageRuleBuilder();

  async function getInteractionRulesForBrands(brandIds) {
    pending.value.interactionRules = true;
    const interactionRuleBrandIds = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
    try {
      const res = await CommunityAPI.getInteractionRulesForBrands(interactionRuleBrandIds);
      interactionRules.value = res.data.data;
    } catch {
      pending.value.interactionRules = false;
    } finally {
      pending.value.interactionRules = false;
    }
  }

  async function getAiCategoriesForBrands(brandIds) {
    if (!brandIds?.length) {
      pending.value.aiCategories = false;
      return;
    }
    pending.value.aiCategories = true;
    try {
      const res = await CommunityAPI.getAiCategoriesForBrands(brandIds);
      aiCategories.value = res.data.data;
    } catch {
      pending.value.aiCategories = false;
    } finally {
      pending.value.aiCategories = false;
    }
  }

  function extractRuleFilters(ruleFilters, filterKey) {
    const items = [];
    if (!ruleFilters?.items?.length) return items;

    ruleFilters.items.forEach((item) => {
      // Match if key matches
      if (item.key === filterKey) {
        items.push(item);
      }
      // Only recurse if item has nested items
      if (item.items) {
        items.push(...extractRuleFilters(item, filterKey));
      }
    });
    return items;
  }

  function updateRuleFilterByKey(ruleFilters, filterKey, updates = {}) {
    if (!ruleFilters?.items?.length) return ruleFilters;

    return {
      ...ruleFilters,
      items: ruleFilters.items.map((item) => {
        const updatedItem = item.key === filterKey ? { ...item, ...updates } : { ...item };

        if (item.items) {
          updatedItem.items = updateRuleFilterByKey(item, filterKey, updates).items;
        }

        return updatedItem;
      }),
    };
  }

  async function createAiCategory({ name, prompt, brandId = selectedBrandId.value }) {
    try {
      const res = await CommunityAPI.createAiCategory({ brandId, name, prompt });
      return res.data;
    } catch (e) {
      logger.error('Error occurred while creating an AI Category', {}, e);
      throw e;
    }
  }

  async function updateRuleFiltersAiCategory(
    payload,
    currentAiCategory,
    pendingValue = 'interactionRule',
  ) {
    // if an unsaved AI category exists, create it first;
    // otherwise, update the existing AI category in the payload to send only its ID
    if (currentAiCategory?.value?.id !== undefined) {
      payload.detail.filters = updateRuleFilterByKey(
        payload.detail.filters,
        AI_CATEGORY_FILTER_OPTION.key,
        {
          value: currentAiCategory.value.id,
        },
      );
      return;
    }
    if (currentAiCategory?.value?.name && currentAiCategory?.value?.prompt) {
      try {
        const aiCategoryRes = await createAiCategory({
          name: currentAiCategory.value.name,
          prompt: currentAiCategory.value.prompt,
          brandId: selectedBrandId.value,
        });
        if (aiCategoryRes?.data) {
          pendingAiCategory.value = aiCategoryRes.data;
          // update the AI category filter in the rule detail filters
          payload.detail.filters = updateRuleFilterByKey(
            payload.detail.filters,
            AI_CATEGORY_FILTER_OPTION.key,
            {
              value: aiCategoryRes.data.id,
            },
          );
        }
      } catch (error) {
        logger.error(
          `Error occurred while creating a new AI Category (brand: ${selectedBrandId.value})`,
          {},
          error,
        );
        pendingAiCategory.value = undefined;
        if (pendingValue) {
          pending.value[pendingValue] = false;
        }
        addToast({
          message: 'Something went wrong. Please try again.',
          severity: 'error',
        });
        throw error;
      }
    }
  }

  async function deleteAiCategory(aiCategoryId) {
    if (aiCategoryId) {
      try {
        await CommunityAPI.deleteAiCategory(aiCategoryId);
        // remove deleted AI category from the AI category list
        remove(aiCategories.value, (c) => c.id === aiCategoryId);
      } catch (categoryErr) {
        logger.error(
          `Error occurred while deleted an AI Category (id: ${aiCategoryId}, brand: ${selectedBrandId.value})`,
          {},
          categoryErr,
        );
      }
    }
  }

  async function createInteractionRule({ name, detail, applyToHistorical = false, successToast }) {
    pending.value.interactionRule = true;
    pendingAiCategory.value = undefined;
    const payload = {
      name,
      detail,
      authorId: identityStore.identity?.id,
      enabled: true,
      applyToHistorical,
    };
    const [currentAiCategory] = extractRuleFilters(
      { ...payload.detail.filters },
      AI_CATEGORY_FILTER_OPTION.key,
    );
    await updateRuleFiltersAiCategory(payload, currentAiCategory);

    try {
      const res = await CommunityAPI.createInteractionRule(selectedBrandId.value, payload);
      interactionRules.value.unshift({
        ...res.data,
        new: true,
      });
      if (pendingAiCategory.value) {
        // add new AI category to the AI category list
        aiCategories.value.push(pendingAiCategory.value);
        pendingAiCategory.value = undefined;
      }
      const toast = successToast.message ? successToast : { message: successToast };
      addToast({ ...toast, severity: 'success' });
      return res.data;
    } catch (error) {
      const errorMessage = getHttpErrorMessage(error);
      const message =
        [409, 400].includes(getHttpErrorStatus(error)) && errorMessage
          ? errorMessage
          : 'Something went wrong. Please try again.';
      addToast({ message, severity: 'error' });

      // delete AI category created on rule creation error
      if (pendingAiCategory.value) {
        await deleteAiCategory(pendingAiCategory.value.id);
        pendingAiCategory.value = undefined;
      }
      throw error;
    } finally {
      pending.value.interactionRule = false;
    }
  }

  async function updateInteractionRule({ ruleId, payload, successToast, ruleModified = false }) {
    pending.value.interactionRule = true;
    try {
      let updatedRule;
      const [existingAiCategoryFilter] = payload.detail
        ? extractRuleFilters({ ...editedRule.value.detail.filters }, AI_CATEGORY_FILTER_OPTION.key)
        : [];
      const [currentAiCategory] = payload.detail
        ? extractRuleFilters({ ...payload.detail.filters }, AI_CATEGORY_FILTER_OPTION.key)
        : [];
      await updateRuleFiltersAiCategory(payload, currentAiCategory);

      try {
        const res = await CommunityAPI.updateInteractionRule(
          selectedBrandId.value,
          ruleId,
          payload,
        );
        updatedRule = res.data;
        const existingRuleIndex = interactionRules.value.findIndex((rule) => rule.id === ruleId);
        if (ruleModified) {
          interactionRules.value.splice(existingRuleIndex, 1);
          interactionRules.value.unshift(updatedRule);
        } else {
          interactionRules.value.splice(existingRuleIndex, 1, updatedRule);
        }
        if (pendingAiCategory.value) {
          // add new AI category to the AI category list
          aiCategories.value.push(pendingAiCategory.value);
        }
        const toast = successToast.message ? successToast : { message: successToast };
        addToast({ ...toast, severity: 'success' });
      } catch (ruleErr) {
        const errorMessage = getHttpErrorMessage(ruleErr);
        const message =
          [409, 400].includes(getHttpErrorStatus(ruleErr)) && errorMessage
            ? errorMessage
            : 'Something went wrong. Please try again.';
        addToast({ message, severity: 'error' });

        // delete AI category created on rule update error
        if (pendingAiCategory.value) {
          await deleteAiCategory(pendingAiCategory.value.id);
          pendingAiCategory.value = undefined;
        }
        throw ruleErr;
      }

      // delete existing AI category if the current AI category has different ID
      if (
        !!updatedRule &&
        !!existingAiCategoryFilter?.value &&
        existingAiCategoryFilter.value !== currentAiCategory?.value?.id
      ) {
        await deleteAiCategory(existingAiCategoryFilter.value);
      }
      pendingAiCategory.value = undefined;
      return updatedRule;
    } finally {
      pending.value.interactionRule = false;
    }
  }

  async function deleteInteractionRule(rule) {
    await CommunityAPI.deleteInteractionRule(selectedBrandId.value, rule.id);
    interactionRules.value = interactionRules.value.filter((r) => r.id !== rule.id);
    messageRuleInteractionsEvent({
      interaction: MESSAGE_RULE_MIXPANEL_CONSTANTS.MESSAGE_RULE_INTERACTIONS.DELETE,
      ruleId: rule.id,
      ruleName: rule.name,
      ruleBrandIds: [selectedBrandId.value],
      ruleBrandNames: [selectedBrandName.value],
    });
  }

  function setEditedRuleId(id) {
    editedRuleId.value = id;
  }

  function openMessageRulePopup() {
    void launchDrawer({ name: 'inboxAutomationRules' });
    messageRulesSelectedEvent({
      ruleBrandIds: [selectedBrandId.value],
      ruleBrandNames: [selectedBrandName.value],
    });
  }

  async function openRuleBuilderToEdit(ruleId, fromMessage = false) {
    setEditedRuleId(ruleId);
    void launchDrawer({ name: 'inboxAutomationRuleWizard' });

    if (editedRule.value) {
      selectedBrandIdForInteractionRules.value = editedRule.value.brandId;
      const option = fromMessage ? 'EDIT_FROM_MESSAGE' : 'EDIT';
      createOrEditMessageRuleEvent({
        openedFrom: MESSAGE_RULE_MIXPANEL_CONSTANTS.RULE_BUILDER_OPENED_FROM[option],
        ruleId,
        ruleName: editedRule.value.name,
        ruleBrandIds: [selectedBrandId.value],
        ruleBrandNames: [selectedBrandName.value],
      });
      messageRuleInteractionsEvent({
        interaction: MESSAGE_RULE_MIXPANEL_CONSTANTS.MESSAGE_RULE_INTERACTIONS[option],
        ruleId,
        ruleName: editedRule.value.name,
        ruleBrandIds: [selectedBrandId.value],
        ruleBrandNames: [selectedBrandName.value],
      });
    }
  }

  function getAuthorName(authorId) {
    if (authorId) {
      const authStore = useAuthStore();
      const brandAccessibleUsers = authStore.brandAccessibleUsers ?? [];
      const author = brandAccessibleUsers.find((u) => u.id === authorId);
      if (author) {
        return [author.firstName, author.lastName].join(' ');
      }
      return 'Unavailable User';
    }
    return 'System';
  }

  async function updateInternalMessageRuleInteraction(interactionId, payload) {
    const communityInteractionStore = useCommunityInteractionStore();
    await communityInteractionStore.updateCommunityInteraction(
      interactionId,
      payload,
      false,
      null,
      null,
      true,
    );
    return true;
  }

  async function deleteInteractionRulesHistory(brandId, ruleExecutionHistoryId) {
    try {
      await CommunityAPI.deleteInteractionRulesHistory(brandId, ruleExecutionHistoryId);
      return true;
    } catch {
      throw new Error('Failed to delete interaction rule history');
    }
  }

  async function undoAction(message, deleteRuleExecutedMessage, undoActionFunction = null) {
    const communityInteractionStore = useCommunityInteractionStore();
    const interactionId = communityInteractionStore.selectedCommunityInteractionId;

    const performUndo = async () => {
      const payload = {};
      const revertPayload = {};
      if (message.action[RULE_ACTION_KEYS.ARCHIVE]) {
        payload.isArchived = false;
        revertPayload.isArchived = true;
      }
      if (message.action[RULE_ACTION_KEYS.TAGS]) {
        const tagIds = Object.keys(message.action[RULE_ACTION_KEYS.TAGS][0]).map(Number);
        payload.tagIds = { remove: tagIds };
        revertPayload.tagIds = { add: tagIds };
      }

      if (message.action[RULE_ACTION_KEYS.HIDE] && undoActionFunction) {
        try {
          await undoActionFunction({
            params: { commentId: interactionId, platform: message.platform },
            data: { isHidden: false },
          });
          // Endpoint we are using here does not accept hide updates, so that will be done separately
          payload.isArchived = false;
          revertPayload.isArchived = true;
        } catch {
          addToast({
            message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.ERROR,
            severity: 'error',
          });
          return;
        }
      }

      if (message.action[RULE_ACTION_KEYS.LIKE] && undoActionFunction) {
        // The like/unlike endpoint isn't part of the interaction payload below,
        // so unlike the comment separately (mirrors the hide branch).
        try {
          await undoActionFunction({
            params: { commentId: interactionId, platform: message.platform },
            data: { isLiked: false },
          });
        } catch {
          addToast({
            message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.ERROR,
            severity: 'error',
          });
          return;
        }
      }

      try {
        // Some actions (e.g. like) have no interaction-level change to undo — the
        // revert is handled entirely by undoActionFunction above — so skip the
        // empty community_interactions PATCH (and its revert) in that case.
        const hasInteractionUpdate = Object.keys(payload).length > 0;
        const undoResult = hasInteractionUpdate
          ? await updateInternalMessageRuleInteraction(interactionId, payload)
          : true;
        if (undoResult) {
          try {
            const deleteHistory = await deleteInteractionRulesHistory(
              identityStore.currentBrand.id,
              message.id,
            );
            if (deleteHistory) {
              deleteRuleExecutedMessage({ ...message, interactionId });
              addToast({
                message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.SUCCESS,
                severity: 'success',
              });
            }
          } catch (error) {
            if (hasInteractionUpdate) {
              await updateInternalMessageRuleInteraction(interactionId, revertPayload);
            }
            throw error;
          }
        }
        undoMessageRuleEvent({
          ruleId: message.ruleId,
          ruleName: message.triggeredRuleName,
          ruleBrandIds: [selectedBrandId.value],
          ruleBrandNames: [selectedBrandName.value],
        });
      } catch {
        addToast({
          message: COMMUNITY_INTERACTION_RULE_TOASTS.UNDO.ERROR,
          severity: 'error',
        });
      }
    };

    await undoRuleActionDialog({
      header: undoAgentActionPopupTitle(isVisionAgentEnabled()),
      message: COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP.confirmMessage,
      acceptLabel: COMMUNITY_INTERACTION_RULES_UNDO_ACTION_POPUP.confirmAlias,
    }).then(async (accepted) => {
      if (accepted) {
        await performUndo();
      }
    });
  }

  function checkRuleDeleted(ruleId) {
    const ruleFound = interactionRules.value.some((rule) => rule.id === ruleId);
    return !ruleFound;
  }

  function validateRuleNameExists(ruleName, ignoreId = null) {
    if (!ruleName) {
      return true;
    }
    let savedRules = interactionRules.value.filter(
      (r) => r.brandId === identityStore.currentBrand?.id,
    );
    if (savedRules) {
      const excludedIds = [editedRuleId.value];
      if (ignoreId) {
        excludedIds.push(ignoreId);
      }
      savedRules = savedRules.filter((r) => !excludedIds.includes(r.id));
    }
    return savedRules.every(({ name }) => name !== ruleName);
  }

  function resetRuleBuilder() {
    clearRuleBuilder();
    setEditedRuleId(undefined);
    switchedToNestedDrawer.value = false;
  }

  return {
    interactionRules,
    pending,
    switchedToNestedDrawer,
    aiCategories,
    selectedBrandAiCategories,
    getBrandAiCategories,
    pendingAiCategory,
    interactionRulesByBrand,
    getSelectedBrandInteractionRules,
    editedRuleId,
    editedRule,
    getInteractionRulesForBrands,
    getAiCategoriesForBrands,
    updateInteractionRule,
    createInteractionRule,
    createAiCategory,
    openMessageRulePopup,
    setEditedRuleId,
    openRuleBuilderToEdit,
    getAuthorName,
    actions,
    conditionGroups,
    isFiltersComplete,
    isActionsComplete,
    hasRepliedFilter,
    filterMatchedMessagesCount,
    ruleDetail,
    ruleChannelSet,
    isSingleChannelRule,
    hasMultiChannelSelection,
    isChannelFilterOrCondition,
    sendDmFromCommentValidation,
    likeCommentsValidation,
    updateGroup,
    removeFilterGroup,
    deleteInteractionRule,
    deleteInteractionRulesHistory,
    undoAction,
    checkRuleDeleted,
    updateGroupOperator,
    addGroupAfter,
    buildJSONRule,
    buildJSONRuleActions,
    buildJSONRuleFilters,
    clearRuleBuilder,
    updateFilterMatchCount,
    checkGroupHasCompleteItem,
    setRuleActionsFromJSON,
    setRuleFiltersFromJSON,
    isRuleDetailChanged,
    getFilterUsed,
    getFilterGroups,
    selectedBrandIdForInteractionRules,
    selectedBrandId,
    selectedBrand,
    selectedBrandName,
    validateRuleNameExists,
    extractRuleFilters,
    updateRuleFilterByKey,
    deleteAiCategory,
    clearFilterMessageCountAndError,
    resetRuleBuilder,
  };
});
